import { getHeader } from '@services/MarppleShop/renderApp/components/cells/Header/libs/helpers/header.ts';
import {
  HeaderBaseState,
  HeaderConstructor,
} from '@services/MarppleShop/renderApp/components/cells/Header/libs/types/header';
import { MakeOptional } from '@services/MarppleShop/shared/type/types.ts';
import { sendDocumentEvent } from '@services/MarppleShop/shared/util/event.ts';
import { $height, $offset } from 'fxdom/es';
import Headroom from 'headroom.js';
import { $Element, View } from 'rune-ts';
import klass from './headroom.module.scss';

export const initHeadroom = (
  headroom_el: HTMLElement,
  headroom_options?: MakeOptional<HeaderBaseState['headroom'], 'use'> & {
    onPin?: () => void;
    onUnpin?: () => void;
    onTop?: () => void;
    onNotTop?: () => void;
    onBottom?: () => void;
    onNotBottom?: () => void;
    disable_hook?: boolean;
  },
) => {
  const headroom = new Headroom(headroom_el, {
    offset: headroom_options?.offset,
    classes: {
      initial: headroom_options?.initial ?? klass.initial,
      pinned: headroom_options?.pinned ?? klass.pinned,
      unpinned: headroom_options?.unpinned ?? klass.unpinned,
      top: headroom_options?.top ?? 'headroom--top',
      notTop: headroom_options?.notTop ?? 'headroom--not-top',
      frozen: headroom_options?.frozen ?? 'headroom--frozen',
    },
    onPin: () => {
      if (headroom_options?.disable_hook) return;
      if (headroom_options?.onPin) {
        headroom_options.onPin();
      } else {
        sendDocumentEvent('header_pinned');
      }
    },
    onUnpin: () => {
      if (headroom_options?.disable_hook) return;
      if (headroom_options?.onUnpin) {
        headroom_options.onUnpin();
      } else {
        sendDocumentEvent('header_unpinned');
      }
    },
    onTop: () => {
      if (headroom_options?.disable_hook) return;
      if (headroom_options?.onTop) {
        headroom_options.onTop();
      } else {
        sendDocumentEvent('header_top');
      }
    },
    onNotTop: () => {
      if (headroom_options?.disable_hook) return;
      if (headroom_options?.onNotTop) {
        headroom_options.onNotTop();
      } else {
        sendDocumentEvent('header_nottop');
      }
    },
    onBottom: () => {
      if (headroom_options?.disable_hook) return;
      if (headroom_options?.onBottom) {
        headroom_options.onBottom();
      } else {
        sendDocumentEvent('header_bottom');
      }
    },
    onNotBottom: () => {
      if (headroom_options?.disable_hook) return;
      if (headroom_options?.onNotBottom) {
        headroom_options.onNotBottom();
      } else {
        sendDocumentEvent('header_notbottom');
      }
    },
  });

  headroom.init();
  return headroom;
};

export const setForceUnpinned = ($headroom_el: $Element | null) => {
  if (!$headroom_el) {
    return false;
  }

  if (!$headroom_el.hasClass(klass.unpinned)) {
    $headroom_el.addClass(klass.unpinned);
    return true;
  }

  return false;
};

export const getOffset = ($main_header: HTMLElement, $sub_header: HTMLElement) => {
  const h_height = $height($main_header);
  const down = $offset($sub_header).top - h_height;
  const up = down;

  return { up, down };
};

export const applySubHeaderHeadroom = (
  header: HeaderConstructor,
  sub_header_view: View,
  option?: { fixed?: boolean },
) => {
  const header_view = getHeader(header);

  if (sub_header_view && header_view) {
    let is_sub_header_headroom_top = false;
    const sub_header_headroom = initHeadroom(sub_header_view.element(), {
      notTop: option?.fixed ? klass.fixed_sub_not_top : klass.sub_not_top,
      pinned: klass.sub_pinned,
      unpinned: klass.sub_unpinned,
      offset: getOffset(header_view.headroom_element ?? header_view.element(), sub_header_view.element()),
      onUnpin: () => {
        if (is_sub_header_headroom_top) {
          sub_header_headroom.pin();
        }
      },
      onNotTop: () => {
        if (header_view.headroom && header_view.headroom_element) {
          header_view.headroom.unfreeze();
        }
        sub_header_view.element().style.transition = '';
        requestAnimationFrame(() => {
          is_sub_header_headroom_top = false;
        });
      },
      onTop: () => {
        requestAnimationFrame(() => {
          is_sub_header_headroom_top = true;
        });
        sub_header_view.element().style.transition = 'unset';
        if (header_view.headroom && header_view.headroom_element) {
          header_view.headroom.freeze();
        }
      },
    });
    header_view.headroom?.freeze();
  }
};
